import { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { classNames } from "primereact/utils";

import { compareValues } from "../../../../../../../utils/functions/compareValues";
import { translationKeys } from "../../../../../../models/app-translation-keys";
import { UploadFile } from "../../../../../../models/classes/upload-file.class";
import { FileUploadComponent, IFileType } from "../../../../../Shared";
import { useSharedDialogState } from "../../../../../Shared/Components/Dialogs/SharedDialog";
import { possibleFileTypes } from "../../../../../Shared/Models/Constants/wellKnownFileTypes.const";
import { IManageGlossaryDialogState } from "../manageGlossaryDialogTypes";

import "./ManageGlossaryDialogContent.scss";

interface IManageGlossaryDialogContentProps {
  minNameLength: number;
}

export const ManageGlossaryDialogContent: FunctionComponent<IManageGlossaryDialogContentProps> = ({
  minNameLength,
}: IManageGlossaryDialogContentProps) => {
  const { t } = useTranslation();
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<IManageGlossaryDialogState>();

  const uploadTranslationKeys = translationKeys.adminArea.glossary;
  const allowedFileTypes: IFileType[] = [possibleFileTypes.csv, possibleFileTypes.xls];

  const onGlossaryNameChanged = (name: string): void => {
    const isDisabled = name.length < minNameLength;

    setSharedDialogState(previousState => {
      if (previousState.glossaryName === name && previousState.isDisabled === isDisabled) {
        return previousState;
      }

      return {
        ...previousState,
        glossaryName: name,
        isDisabled,
      };
    });
  };

  const onGlossaryDescriptionChanged = (description: string): void => {
    setSharedDialogState(previousState => {
      if (previousState.glossaryDescription === description) {
        return previousState;
      }

      return {
        ...previousState,
        glossaryDescription: description,
      };
    });
  };

  const onGlossaryFileChanged = (files: UploadFile[]): void => {
    const glossaryFile = files?.[0] ?? null;

    setSharedDialogState(previousState => {
      if (compareValues(previousState.glossaryFile, glossaryFile) === 0) {
        return previousState;
      }

      return {
        ...previousState,
        glossaryFile,
      };
    });
  };

  return (
    <Fragment>
      <div className="manage-glossary-form-element">
        <label>{t(uploadTranslationKeys.glossaryTable.glossary.name)}</label>

        <InputText
          onChange={e => onGlossaryNameChanged(e.target.value)}
          value={sharedDialogState.glossaryName}
          className={classNames({
            "p-invalid": sharedDialogState.glossaryName.length < minNameLength,
          })}
          maxLength={150}
        />
      </div>

      <div className="manage-glossary-form-element">
        <label>{t(uploadTranslationKeys.glossaryTable.glossary.description)}</label>

        <InputTextarea
          onChange={e => onGlossaryDescriptionChanged(e.target.value)}
          value={sharedDialogState.glossaryDescription}
          rows={3}
          maxLength={800}
        />
      </div>

      <FileUploadComponent
        allowedFileTypes={allowedFileTypes}
        isSingleFileUpload={true}
        maxConcurrentProcessedFiles={1}
        onChange={onGlossaryFileChanged}
        fileSizeLimitMB={100}
        smallFileList={true}
        disabled={sharedDialogState.isUploading}
      />
    </Fragment>
  );
};
